/**
 * <s-product-gallery> — PDP main image with thumbnails underneath.
 *
 * Only one slide is visible at a time. Thumbnail buttons select a slide through
 * the Invoker Commands API (commandfor + command="--show-media", value = index).
 * On devices with a fine pointer, hovering a thumbnail previews its slide.
 *
 * Required markup:
 *   [data-gallery-slide="n"] — one per media item; all but the active one carry `hidden`.
 *   button[data-gallery-thumb="n"][aria-pressed] — thumbnail buttons.
 */
class SProductGallery extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac?.abort();
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', (event) => {
      if (event.command !== '--show-media') return;
      const index = Number(event.source?.value);
      if (Number.isInteger(index)) this.#show(index);
    }, opts);

    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.addEventListener('pointerover', (event) => {
      if (!finePointer.matches || event.pointerType !== 'mouse') return;
      if (!(event.target instanceof Element)) return;
      const thumb = event.target.closest('[data-gallery-thumb]');
      if (!thumb || !this.contains(thumb)) return;
      this.#show(Number(thumb.dataset.galleryThumb));
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #show(index) {
    const slides = this.querySelectorAll('[data-gallery-slide]');
    if (!slides[index] || !slides[index].hidden) return;

    for (const slide of slides) {
      const active = Number(slide.dataset.gallerySlide) === index;
      if (!active && !slide.hidden) {
        for (const video of slide.querySelectorAll('video')) video.pause();
      }
      slide.hidden = !active;
      if (active) {
        for (const img of slide.querySelectorAll('img[loading="lazy"]')) img.loading = 'eager';
      }
    }

    for (const thumb of this.querySelectorAll('[data-gallery-thumb]')) {
      thumb.setAttribute('aria-pressed', String(Number(thumb.dataset.galleryThumb) === index));
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
